/**
 * MobileTopNav — the single global top bar for mobile/tablet tab roots.
 *
 * One bar, at the very top, full-bleed, on every tab root (/explore, /trips,
 * /tours, /hosting, /messages, /services). It replaces the greeting header that
 * used to live INSIDE Explore's ScrollView, rounded and with side margins, one
 * instance for one screen.
 *
 * WHY IT CANNOT COVER ANYTHING. It is a flex SIBLING of the navigator (see
 * `AppShell`), never `position: absolute`: the scene starts below it, so it is
 * fixed against scroll by construction and there is no scroll offset or
 * safe-area combination that can put content under it. The top safe area is
 * spent here once — `TopNavInsetProvider` publishes it so screens stop paying
 * it again.
 *
 * NO BORDERS. `GlassSurface` ships a 1px illuminated edge; at full bleed the
 * bottom edge reads as a separator line, which is exactly what this bar must
 * not have. The four border colors are overridden to zero/transparent instead
 * of duplicating the material.
 *
 * THE `explore.*` TEST IDS ARE HISTORICAL. This bar was born inside Explore and
 * sixteen Maestro flows, two capture scripts and `validate-product-feel` depend
 * on those exact ids. Renaming them for symmetry would break the E2E suite for
 * a cosmetic win.
 */
import React, { useCallback, useMemo } from 'react'
import { Text, View } from 'react-native'
import { usePathname, useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { Avatar } from '@/components/ui/Avatar'
import { CountBadge } from '@/components/ui/CountBadge'
import { GlassSurface } from '@/components/ui/GlassSurface'
import { Pressable } from '@/components/ui/Pressable'
import { Bell, MessageCircle } from '@/components/ui/icons'
import { COLORS, FONTS, RADIUS, SPACING } from '@/lib/brand'
import { useAuth } from '@/lib/hooks/use-auth'
import { useNotificationsUnreadCount } from '@/lib/hooks/use-notifications-unread'
import { useTheme } from '@/lib/hooks/use-theme'
import { useUnreadCount } from '@/lib/hooks/use-unread-count'
import { getExploreAvatarRoute } from '@/lib/navigation/explore-avatar-route'
import { shouldShowMobileTopNav } from '@/lib/navigation/mobile-top-nav'
import { MOBILE_TOP_NAV_HEIGHT } from '@/lib/theme/chrome'
import { useSemanticColors } from '@/lib/theme/tokens'
import { WEB_SHADOWS } from '@/lib/theme/web-shadow'
import { getCurrentProfileDisplay } from '@/lib/utils/profile-display'

function getGreetingKey(): string {
  const h = new Date().getHours()
  if (h < 12) return 'explore.greeting_morning'
  if (h < 18) return 'explore.greeting_afternoon'
  return 'explore.greeting_evening'
}

export function MobileTopNav() {
  const { t } = useTranslation()
  const router = useRouter()
  const pathname = usePathname()
  const insets = useSafeAreaInsets()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { user, profile, isAuthenticated } = useAuth()
  const unreadMessages = useUnreadCount()
  const unreadNotifications = useNotificationsUnreadCount()
  const profileDisplay = useMemo(() => getCurrentProfileDisplay({ profile, user }), [profile, user])

  const handleOpenProfile = useCallback(
    () => router.navigate(getExploreAvatarRoute(isAuthenticated)),
    [isAuthenticated, router],
  )
  const handleOpenMessages = useCallback(() => router.navigate('/messages'), [router])
  const handleOpenNotifications = useCallback(() => router.push('/profile/notifications'), [router])

  // Hooks above, decision here: an early return before the hooks would break
  // their order the moment a route change flips the predicate.
  if (!shouldShowMobileTopNav(pathname)) return null

  const badgeRingColor = isDark ? colors.surfaceRaised : COLORS.white

  return (
    <GlassSurface
      testID="explore.header"
      tone="regular"
      visualRole="chrome"
      radius={0}
      style={{
        width: '100%',
        // A full-bleed plane has no edges to illuminate: the bottom hairline of
        // the material would read as a separator rule.
        borderWidth: 0,
        borderColor: 'transparent',
        borderTopColor: 'transparent',
        borderBottomColor: 'transparent',
      }}
      contentStyle={{
        paddingTop: insets.top + SPACING.sm,
        paddingBottom: SPACING.sm,
        paddingHorizontal: SPACING.lg,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: SPACING.md,
        // The bar's chrome without the top inset; the same constant the toast
        // host clears, so the two cannot drift apart.
        minHeight: MOBILE_TOP_NAV_HEIGHT - SPACING.sm * 2,
      }}
    >
      <Pressable
        onPress={handleOpenProfile}
        accessibilityRole="button"
        accessibilityLabel={t('tabs.profile')}
        testID="explore.avatar"
        style={{
          flex: 1,
          minWidth: 0,
          minHeight: 44,
          flexDirection: 'row',
          alignItems: 'center',
          gap: SPACING.sm + 2,
        }}
      >
        <View
          style={{
            padding: 2,
            borderRadius: RADIUS.xl - 1,
            borderWidth: 1,
            borderColor: colors.border,
            // The chips sit on the glass bar, so they take the next step up or
            // they melt into their own material.
            backgroundColor: isDark ? colors.surfaceRaised : COLORS.white,
            ...WEB_SHADOWS.sm,
          }}
        >
          <Avatar
            size="md"
            source={profileDisplay.avatarUrl}
            blurhash={profileDisplay.avatarBlurhash ?? undefined}
            fallback={profileDisplay.fallback}
            colorSeed={profileDisplay.id}
            accessibilityLabel={profileDisplay.accessibilityLabel}
          />
        </View>
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text
            style={{
              fontSize: 13,
              color: colors.textSecondary,
              fontFamily: FONTS.body,
            }}
            numberOfLines={1}
          >
            {t(getGreetingKey())}
          </Text>
          <Text
            style={{ fontSize: 16, color: colors.textPrimary, fontFamily: FONTS.bodyBold }}
            numberOfLines={1}
          >
            {profileDisplay.firstName}
          </Text>
        </View>
      </Pressable>

      <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.xs }}>
        <Pressable
          onPress={handleOpenMessages}
          accessibilityRole="button"
          accessibilityLabel={t('tabs.messages')}
          testID="explore.messages-button"
          style={{
            width: 44,
            height: 44,
            borderRadius: RADIUS.lg,
            position: 'relative',
          }}
        >
          <GlassSurface
            tone="quiet"
            radius={RADIUS.lg}
            interactive
            style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}
          >
            <MessageCircle size={18} color={colors.textPrimary} strokeWidth={1.8} />
            <View style={{ position: 'absolute', top: 2, right: 2 }}>
              <CountBadge
                count={unreadMessages}
                ringColor={badgeRingColor}
                testID="explore.unread-messages"
              />
            </View>
          </GlassSurface>
        </Pressable>
        <Pressable
          onPress={handleOpenNotifications}
          accessibilityRole="button"
          accessibilityLabel={t('explore.notifications')}
          testID="explore.notifications-button"
          style={{
            width: 44,
            height: 44,
            borderRadius: RADIUS.lg,
            position: 'relative',
          }}
        >
          <GlassSurface
            tone="quiet"
            radius={RADIUS.lg}
            interactive
            style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}
          >
            <Bell size={18} color={colors.textPrimary} strokeWidth={1.8} />
            <View style={{ position: 'absolute', top: 2, right: 2 }}>
              <CountBadge
                count={unreadNotifications}
                ringColor={badgeRingColor}
                testID="explore.unread-notifications"
              />
            </View>
          </GlassSurface>
        </Pressable>
      </View>
    </GlassSurface>
  )
}
